import { FontFamilies, Spacing } from '@persona-id/design-tokens';
import { ClipboardIcon } from '@persona-id/design-tokens/icons';
import { Fragment, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Field, FormSection } from 'redux-form';
import { FONT_SIZES } from 'shared/styles/variables';
import styled from 'styled-components';

import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import InputWithHoverEditField from 'dashboard/components/FormControls/InputWithHoverEditField';
import ProductUpsellTooltip from 'dashboard/components/ProductUpsellTooltip';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import copyToClipboard from 'dashboard/lib/utils/copyToClipboard';
import { showMessage } from 'dashboard/redux/actions/messages';
import Button from 'design-system/components/Button';
import { Heading, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';

type PanelHeaderSubtitles = 'Component' | 'Device Handoff' | 'Screen';
type PanelHeaderTitleProps = {
  title: string;
  subtitle: PanelHeaderSubtitles;
  editable?: boolean;
  basePath?: string;
  handleDelete?: () => void;
  sticker?: React.ReactNode;
  copyText?: string;
};

const PanelHeaderTitleWrapper = styled.div`
  margin-bottom: ${Spacing[1]};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FieldWrapper = styled.div`
  width: 100%;

  & > div {
    min-height: auto;
  }
`;

const SubtitleWrappper = styled.div`
  display: grid;
  grid-gap: ${Spacing[2]};
  grid-template-rows: 100%;
  grid-template-columns: max-content min-content min-content 1fr;
  align-items: center;
`;

const CopyButton = styled(Button).attrs(() => ({
  type: 'button',
  variant: 'inverse',
  width: 'icon',
  size: 'sm',
}))`
  margin-left: -2px;
  svg {
    width: 14px;
    height: 14px;
  }
`;

const ClippedParagraph = styled(Paragraph)`
  text-overflow: ellipsis;
  max-width: 200px;
  overflow: hidden;
  text-wrap: nowrap;
  font-family: ${FontFamilies.Monospace};
  font-size: ${FONT_SIZES.code};
  line-height: ${({ theme }) => theme.lineHeightBody};
`;

export const PanelHeaderSection = styled.div`
  background-color: ${({ theme }) => theme.color.background.primary};
  border-bottom: ${({ theme }) => `1px solid ${theme.color.border.primary}`};
  padding: ${Spacing[5]};
  z-index: 1;
`;

export const PanelHeaderTitle = ({
  title,
  subtitle,
  editable,
  basePath,
  sticker,
  copyText,
}: PanelHeaderTitleProps) => {
  const [canEdit] = useState(basePath != null && editable);
  const dispatch = useDispatch();

  const canCopyText = copyText != null && subtitle === 'Component';

  const dispatchCopyCallbackToast = (wasCopySuccessful: boolean) => {
    const message = wasCopySuccessful
      ? showMessage(`Copied ${copyText} to clipboard`, MessageType.Success)
      : showMessage(`Failed copy ${copyText} to clipboard`, MessageType.Failure);
    dispatch(message);
  };

  const copyTextToClipboard = async () => {
    if (!canCopyText) return false;
    const copySuccessful = await copyToClipboard(copyText as string);
    return dispatchCopyCallbackToast(copySuccessful);
  };

  return (
    <>
      <PanelHeaderTitleWrapper>
        {canEdit && (
          <FormSection name='steps' component={Fragment}>
            <FieldWrapper>
              <Field
                type='text'
                name={`${basePath}.name-display`}
                component={InputWithHoverEditField}
                displayElement={<Heading size='sm' />}
                disabled={!editable}
                placeholder={title}
                autoFocus
              />
            </FieldWrapper>
          </FormSection>
        )}
        {!canEdit && (
          <>
            <Heading size='sm'>{title}</Heading>
            {sticker && (
              <ProductUpsellTooltip
                side='left'
                accessTier={EditorAccessTier.InquiriesBasic}
                learnMoreId='panel-header-title__title'
                trigger={<div>{sticker}</div>}
              />
            )}
          </>
        )}
      </PanelHeaderTitleWrapper>
      <SubtitleWrappper>
        {!canCopyText && <Paragraph>{subtitle}</Paragraph>}
        {canCopyText && (
          <>
            <ClippedParagraph>{copyText}</ClippedParagraph>
            <Tooltip
              align='center'
              side='top'
              trigger={
                <CopyButton id='panel-header-title__copy-text-button' onClick={copyTextToClipboard}>
                  <ClipboardIcon width={16} height={16} />
                </CopyButton>
              }
            >
              <Paragraph>Copy component name</Paragraph>
            </Tooltip>
          </>
        )}
      </SubtitleWrappper>
    </>
  );
};
